import type { Composer } from 'vue-i18n';
import { inject, injectable } from 'inversify';
import type { SideNavItem, SideNavSection } from '@design-system/side-nav/application/component/SideNav/SideNav.types';
import { MessagingRouteName } from '@client/feature/messaging/application/routes/messaging.route-name';
import { AttendeeManagementRouteName } from '@client/feature/attendee-management/application/routes/attendee-management.route-name';
import { CommunicationKitRouteName } from '@client/feature/communication-kit/application/routes/communication-kit.route-name';
import { UsageDataRouteName } from '@client/feature/usage-data/application/routes/usage-data.route-name';
import { EstablishmentRouteName } from '@client/feature/establishment/application/routes/establishment.route-name';
import { TranslationDependencyTypes } from '@kiplin/web-common/translation';
import { IconName } from '@kiplin/web-common/icon';
import type { CurrentClientUserClient } from '@client/entity/current-client-user/domain/model/current-client-user-client.model';
import { TrackingDependencyTypes } from '@shared/common/tracking/tracking.dependency-types';
import type { TrackingService } from '@shared/common/tracking/domain/service/tracking.service';
import { FeatureFlagKey } from '@client/common/feature-flags/domain/constant/feature-flag-key.constant';
import { COCKPIT_NAVIGATION_SECTION_IDS } from '@client/composition/main-layout/domain/constant/cockpit-navigation-section-ids.constant';

@injectable()
export class CockpitNavigationService {
  public constructor(
    @inject(TranslationDependencyTypes.i18n)
    private readonly i18n: Composer,
    @inject(TrackingDependencyTypes.TrackingService)
    private readonly trackingService: TrackingService,
  ) {}

  public getDefaultSections(): SideNavSection[] {
    const sections: SideNavSection[] = [];

    sections.push({
      id: COCKPIT_NAVIGATION_SECTION_IDS.COMMUNICATION,
      title: this.i18n.t('common.side-nav.my-kits'),
      dividerAfter: false,
      items: [
        {
          id: 'messaging',
          label: this.i18n.t('common.side-nav.messaging'),
          icon: IconName.Chat,
          associatedRouteName: MessagingRouteName.ClientMessagesPage,
        },
        {
          id: 'communication-kit',
          label: this.i18n.t('common.side-nav.create-kit'),
          icon: IconName.Image,
          associatedRouteName: CommunicationKitRouteName.Template,
        },
        {
          id: 'programmation',
          label: this.i18n.t('common.side-nav.programmation'),
          icon: IconName.Calendar,
          outsideLink: true,
          href: this.i18n.t('common.side-nav.links.programmation'),
        },
        {
          id: 'tools',
          label: this.i18n.t('common.side-nav.tools'),
          icon: IconName.Mail,
          outsideLink: true,
          href: this.i18n.t('common.side-nav.links.tools'),
        },
        {
          id: 'guide-kit',
          label: this.i18n.t('common.side-nav.guide'),
          icon: IconName.News,
          outsideLink: true,
          href: this.i18n.t('common.side-nav.links.guide'),
        },
      ],
    });

    return sections;
  }

  public getSubscriptionSection(userClient: CurrentClientUserClient): SideNavSection {
    const subscriptionItems: SideNavItem[] = [
      {
        id: 'usages',
        label: this.i18n.t('common.side-nav.usage'),
        icon: IconName.Stat,
        associatedRouteName: UsageDataRouteName.Dashboard,
      },
      {
        id: 'attendees',
        label: this.i18n.t('common.side-nav.subscriptions'),
        icon: IconName.Team,
        associatedRouteName: AttendeeManagementRouteName.AttendeesList,
      },
    ];

    if (this.trackingService.isFeatureEnabled(FeatureFlagKey.Establishment)) {
      subscriptionItems.push({
        id: 'establishments',
        label: this.i18n.t('common.side-nav.establishments'),
        icon: IconName.City,
        associatedRouteName: EstablishmentRouteName.List,
      });
    }

    return {
      id: COCKPIT_NAVIGATION_SECTION_IDS.SUBSCRIPTION,
      title: this.i18n.t('common.side-nav.my-subscription'),
      subtitle: this.getSubscriptionSubtitle(userClient.subscriptionStartDate, userClient.subscriptionEndDate),
      dividerAfter: true,
      items: subscriptionItems,
    };
  }

  public getGameSection(items: SideNavItem[]): SideNavSection {
    return {
      id: COCKPIT_NAVIGATION_SECTION_IDS.GAMES,
      title: this.i18n.t('common.side-nav.my-games'),
      items: items.length ? items : [this.getEmptyGamesItem()],
    };
  }

  public getEmptyGamesItem(): SideNavItem {
    return {
      id: 'no-games',
      label: this.i18n.t('common.side-nav.no-games'),
      icon: IconName.Warning,
      associatedRouteName: 'cockpit-home',
    };
  }

  private getSubscriptionSubtitle(subscriptionStartDate: string | null, subscriptionEndDate: string | null): string[] {
    if (!subscriptionStartDate) {
      return [];
    }
    const subtitle: string[] = [];

    if (subscriptionStartDate) {
      subtitle.push(
        this.i18n.t('subscription.started_since', {
          date: this.i18n.d(subscriptionStartDate, 'calendarDate'),
        }),
      );
    }

    if (subscriptionEndDate) {
      subtitle.push(
        this.i18n.t('subscription.ends_on', {
          date: this.i18n.d(subscriptionEndDate, 'calendarDate'),
        }),
      );
    }

    return subtitle;
  }
}
